'use strict'

import {
  mdiDownload,
  mdiFileDocumentOutline,
  mdiFileOutline,
  mdiImageOutline,
  mdiMovieOpen,
  mdiMusicNote
} from '@mdi/js'

export const ATTACHMENT_ICON_SIZE = {
  container: 56,
  icon: 28,
  borderRadius: 14
}

type AttachmentCategory = 'audio' | 'video' | 'image' | 'document' | 'default'

const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac']
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm']
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp']
const DOCUMENT_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
  'rtf'
]

const getExtension = (fileName: string) => {
  const parts = fileName.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

const getCategoryFromExtension = (ext: string): AttachmentCategory => {
  if (AUDIO_EXTENSIONS.includes(ext)) return 'audio'
  if (VIDEO_EXTENSIONS.includes(ext)) return 'video'
  if (IMAGE_EXTENSIONS.includes(ext)) return 'image'
  if (DOCUMENT_EXTENSIONS.includes(ext)) return 'document'
  return 'default'
}

const getCategory = (
  mimeType: string,
  fileName: string
): AttachmentCategory => {
  const mime = mimeType.toLowerCase()
  if (mime.startsWith('audio/')) return 'audio'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('application/') || mime.startsWith('text/')) {
    return 'document'
  }

  if (mime && !mime.includes('/')) {
    return getCategoryFromExtension(mime)
  }

  return getCategoryFromExtension(getExtension(fileName))
}

export const getAttachmentIcon = (
  mimeType: string,
  fileName: string,
  isHovered: boolean
) => {
  if (isHovered) return mdiDownload

  switch (getCategory(mimeType, fileName)) {
    case 'audio':
      return mdiMusicNote
    case 'video':
      return mdiMovieOpen
    case 'image':
      return mdiImageOutline
    case 'document':
      return mdiFileDocumentOutline
    default:
      return mdiFileOutline
  }
}

export const formatAttachmentType = (mimeType: string, fileName: string) => {
  if (mimeType && !mimeType.includes('/')) {
    return mimeType.toUpperCase()
  }

  const subtype = mimeType.split('/')[1]
  if (subtype) return subtype.toUpperCase()

  const ext = getExtension(fileName)
  return ext ? ext.toUpperCase() : 'FILE'
}

export const formatAttachmentSize = (fileSize: number) => {
  if (!fileSize) return ''

  const units = ['B', 'KB', 'MB', 'GB']
  let size = fileSize
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  const rounded = unitIndex === 0 ? size : Math.round(size * 10) / 10
  return `${rounded} ${units[unitIndex]}`
}

export const downloadFile = async (url: string, fileName: string) => {
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.setAttribute('download', fileName)
    anchor.href = blobUrl
    document.body.appendChild(anchor)
    anchor.click()
    anchor.parentElement?.removeChild(anchor)
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}
